import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, UserPlus, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { colorCls, fmtAge } from "@/lib/format";
import { useNow } from "@/hooks/useNow";

export function UnitsPanel({ incident, units, statuses, readOnly }) {
  const now = useNow(1000);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  const live = incident.status === "pending" || incident.status === "active";
  const assignedIds = new Set(incident.assigned_units.map((u) => u.unit_id));
  const rec = incident.recommendation;

  const assign = async (unitIds, via) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/incidents/${incident.id}/assign`, { unit_ids: unitIds, via });
      toast.success(`${data.assigned.join(", ")} assigned to ${incident.incident_number}${incident.status === "pending" ? " — call is now ACTIVE" : ""}`);
      setPick("");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (u) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/incidents/${incident.id}/unassign`, { unit_id: u.unit_id });
      toast.success(`${u.call_sign} removed${data.status === "pending" ? " — call returned to PENDING" : ""}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const setStatus = async (u, status_id) => {
    if (!status_id) return;
    try { await api.post(`/units/${u.unit_id}/status`, { status_id, via: "workspace" }); } catch (e) { toast.error(errMsg(e)); }
  };

  const candidates = units.filter((u) => !assignedIds.has(u.id));
  const sortedCandidates = [...candidates].sort((a, b) => (a.status.category === "available" ? 0 : 1) - (b.status.category === "available" ? 0 : 1) || a.call_sign.localeCompare(b.call_sign));

  return (
    <div className="space-y-1.5" data-testid="units-panel">
      {live && !readOnly && rec && (
        <div className={`rounded-md border px-2.5 py-1.5 text-xs flex items-center gap-2 ${rec.unit_id ? "border-primary/50 bg-primary/10" : "border-amber-500/50 bg-amber-500/10"}`} data-testid="recommendation-banner">
          <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <span className="overline-label mr-1">Recommended</span>
            {rec.unit_id ? <><span className="mono font-semibold text-sm" data-testid="recommended-unit">{rec.call_sign}</span> <span className="text-muted-foreground">· {rec.reason}</span></> : <span className="text-muted-foreground" data-testid="recommendation-reason">{rec.reason}</span>}
          </div>
          {rec.unit_id && !assignedIds.has(rec.unit_id) && (
            <button data-testid="assign-recommended-button" disabled={busy} onClick={() => assign([rec.unit_id], "recommendation")} className="h-7 px-2.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">Assign {rec.call_sign}</button>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="overline-label mr-1">Units</span>
        {incident.assigned_units.length === 0 && <span className="text-xs text-muted-foreground" data-testid="no-units-label">None assigned</span>}
        {incident.assigned_units.map((u) => {
          const full = units.find((x) => x.id === u.unit_id);
          const opts = statuses.filter((s) => s.discipline === full?.discipline);
          return (
            <span key={u.unit_id} data-testid={`assigned-unit-${u.call_sign}`} className={`chip pl-2 ${colorCls(u.status_color)}`}>
              <span className="mono text-xs font-semibold">{u.call_sign}</span>
              {readOnly ? <span>· {u.status_label}</span> : (
                <select data-testid={`assigned-unit-status-${u.call_sign}`} value={full?.status?.id || ""} onChange={(e) => setStatus(u, e.target.value)} className="bg-transparent text-[11px] outline-none">
                  {opts.map((s) => <option key={s.id} value={s.id} className="text-foreground bg-popover">{s.label}</option>)}
                </select>
              )}
              {full && <span className="mono text-[10px] opacity-80">{fmtAge(full.status_since, now)}</span>}
              {!readOnly && live && <button data-testid={`remove-unit-${u.call_sign}`} disabled={busy} onClick={() => remove(u)} className="ml-0.5 rounded hover:bg-background/60" title="Remove from incident"><X className="h-3 w-3" /></button>}
            </span>
          );
        })}
        {live && !readOnly && (
          <span className="inline-flex items-center gap-1 ml-auto">
            <select data-testid="assign-unit-select" value={pick} onChange={(e) => setPick(e.target.value)} className="h-7 rounded-md border bg-background text-xs px-1.5 max-w-[220px]">
              <option value="">Assign unit…</option>
              {sortedCandidates.map((u) => <option key={u.id} value={u.id}>{u.call_sign} · {u.status.label}{u.incident ? ` (on ${u.incident.incident_number})` : ""}</option>)}
            </select>
            <button data-testid="assign-unit-button" disabled={!pick || busy} onClick={() => assign([pick], "button")} className="h-7 px-2 rounded-md border text-xs font-medium hover:bg-accent disabled:opacity-50 flex items-center gap-1"><UserPlus className="h-3.5 w-3.5" /> Assign</button>
          </span>
        )}
      </div>
    </div>
  );
}
